/**
 * Pure utility functions for role-member computations.
 * Used by the Role page to derive member counts, filter assigned/unassigned users.
 */

export const SMALL_LOCATION_THRESHOLD = 15;

/**
 * Get all members assigned to a specific role.
 * @param {Array} members - Array of user privilege objects
 * @param {number} roleId - The role tag ID
 * @returns {Array} Members whose user.tag_ids includes roleId
 */
export const getMembersForRole = (members, roleId) => {
  if (!members || !roleId) return [];
  return members.filter((member) => {
    const tagIds = member.user?.tag_ids || [];
    return tagIds.includes(roleId);
  });
};

/**
 * Get all members NOT assigned to a specific role.
 * @param {Array} members - Array of user privilege objects
 * @param {number} roleId - The role tag ID
 * @returns {Array} Members whose user.tag_ids does NOT include roleId
 */
export const getUnassignedMembersForRole = (members, roleId) => {
  if (!members || !roleId) return [];
  return members.filter((member) => {
    const tagIds = member.user?.tag_ids || [];
    return !tagIds.includes(roleId);
  });
};

/**
 * Get the count of members assigned to a specific role.
 * @param {Array} members - Array of user privilege objects
 * @param {number} roleId - The role tag ID
 * @returns {number}
 */
export const getMemberCountForRole = (members, roleId) => {
  return getMembersForRole(members, roleId).length;
};

/**
 * Determine if the location is "small" based on total approved member count.
 * @param {Array} members - Array of user privilege objects
 * @returns {boolean} true if <= SMALL_LOCATION_THRESHOLD
 */
export const isSmallLocation = (members) => {
  if (!members) return true;
  return members.length <= SMALL_LOCATION_THRESHOLD;
};

/**
 * Get a display name for a member.
 * @param {Object} member - User privilege object
 * @returns {string}
 */
export const getMemberDisplayName = (member) => {
  const first = member.first_name || '';
  const last = member.last_name || '';
  return `${first} ${last}`.trim() || 'Unknown';
};

/**
 * Filter members by search term (matches first_name or last_name, case-insensitive).
 * @param {Array} members - Array of user privilege objects
 * @param {string} searchTerm - The search query
 * @returns {Array}
 */
export const filterMembersBySearch = (members, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return members;
  const term = searchTerm.toLowerCase().trim();
  return members.filter((member) => {
    const first = (member.first_name || '').toLowerCase();
    const last = (member.last_name || '').toLowerCase();
    const full = `${first} ${last}`;
    return first.includes(term) || last.includes(term) || full.includes(term);
  });
};
